import { useEffect, useRef } from "react";
import { useServerFn } from "@tanstack/react-start";
import { recordQrScan } from "@/lib/qr-scan.functions";
import { isDemoActive } from "@/lib/demo/demo-runtime";
import { QR_SCAN_PARAM_VALUE } from "@/lib/qr-scan";

const SESSION_KEY = "sa_session_id";
const SEEN_PREFIX = "sa_qr:";

/** Reuse (or mint) the same per-tab-session identifier used by profile views. */
function getSessionId(): string | null {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return null;
  }
}

/**
 * Fires ONE `qr_code_scan` per nominee per browser session when the visitor
 * arrives at the QR destination carrying the QR attribution parameter.
 *
 * The sessionStorage guard only avoids redundant network calls; the server
 * re-checks the same session id against persisted events, so
 * `recognition_events` remains the single source of truth.
 */
export function useQrScan(
  slug: string | null | undefined,
  src: string | null | undefined,
) {
  const record = useServerFn(recordQrScan);
  const fired = useRef<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    if (String(src ?? "").toLowerCase() !== QR_SCAN_PARAM_VALUE) return;
    // The read-only demonstration must never write a production event.
    if (isDemoActive()) return;
    if (fired.current === slug) return;
    fired.current = slug;

    const sessionId = getSessionId();
    if (!sessionId) return;
    const seenKey = `${SEEN_PREFIX}${slug}`;
    try {
      if (window.sessionStorage.getItem(seenKey)) return;
    } catch {
      /* private mode — the server-side session dedupe still applies */
    }

    let cancelled = false;
    record({
      data: {
        slug,
        src: QR_SCAN_PARAM_VALUE,
        sessionId,
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
      },
    })
      .then(() => {
        // Only mark the session as handled once the server has actually
        // answered, so a dropped request never silently burns the one
        // qualifying scan this session is allowed to record.
        try {
          window.sessionStorage.setItem(seenKey, "1");
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        // Transport failure: allow a later mount in this session to retry.
        if (!cancelled) fired.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [slug, src, record]);
}

